import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  ExternalLink,
  Package,
  RefreshCw,
  Ship,
  Truck,
} from "lucide-react";
import API from "../../../util/API";
import AdminPageShell from "../components/AdminPageShell";
import ADMIN_PATHS from "../ADMIN_PATHS";

const cx = (...arr) => arr.filter(Boolean).join(" ");

const Card = ({ children, className }) => (
  <div className={cx("bg-white border border-slate-200 rounded-2xl", className)}>{children}</div>
);

const SecondaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed",
      className
    )}
  />
);

const MetricCard = ({ title, value, sub, icon: Icon, tone = "slate" }) => {
  const toneStyles = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    sky: "bg-sky-50 border-sky-200 text-sky-700",
    violet: "bg-violet-50 border-violet-200 text-violet-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
  };
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <div
          className={cx(
            "h-10 w-10 rounded-2xl border flex items-center justify-center",
            toneStyles[tone] || toneStyles.slate
          )}
        >
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="mt-4 text-xs font-black uppercase tracking-[0.18em] text-slate-500">{title}</p>
      <p className="mt-2 text-2xl font-black tracking-tight text-slate-900">{value}</p>
      {sub ? <p className="mt-1 text-xs text-slate-500">{sub}</p> : null}
    </Card>
  );
};

const STATE_TONE = {
  delivered: "border-emerald-200 bg-emerald-50 text-emerald-800",
  in_transit: "border-sky-200 bg-sky-50 text-sky-800",
  awaiting_shipment: "border-amber-200 bg-amber-50 text-amber-800",
};

const STATE_LABEL = {
  delivered: "Delivered",
  in_transit: "In transit",
  awaiting_shipment: "Awaiting shipment",
};

const formatDateTime = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatRelative = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "Never";
  const diff = Date.now() - date.getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

export default function ShippingOverview() {
  const [overview, setOverview] = useState(null);
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 25, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshingId, setRefreshingId] = useState(null);
  const [refreshMsg, setRefreshMsg] = useState("");

  const load = useCallback(
    async (targetPage = 1) => {
      setLoading(true);
      setError("");
      try {
        const [overviewRes, ordersRes] = await Promise.all([
          API.get("/aliexpress-tracking/admin/shipping-overview"),
          API.get("/aliexpress-tracking/admin/shipping-orders", {
            params: { page: targetPage, limit: pagination.limit },
          }),
        ]);
        setOverview(overviewRes?.data?.data || null);
        const rows = Array.isArray(ordersRes?.data?.data) ? ordersRes.data.data : [];
        setOrders(rows);
        if (ordersRes?.data?.pagination) {
          setPagination(ordersRes.data.pagination);
        } else {
          setPage(targetPage);
        }
      } catch (err) {
        setError(err?.response?.data?.message || err?.message || "Failed to load shipping data.");
      } finally {
        setLoading(false);
      }
    },
    [pagination.limit]
  );

  useEffect(() => {
    load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const refreshOrderTracking = useCallback(async (order) => {
    setRefreshingId(order.id);
    setRefreshMsg("");
    try {
      // Reuse existing protect+admin routes: read the AE order mapping, then
      // pull fresh tracking for those AE order ids (OrderDetailsPage pattern).
      const mapRes = await API.get("/aliexpress-tracking/map", {
        params: { orderMongoId: order.id, orderId: order.orderId || undefined },
      });
      const aeOrderIds = Array.isArray(mapRes?.data?.data?.order_ids)
        ? mapRes.data.data.order_ids
        : [];
      if (!aeOrderIds.length) {
        setRefreshMsg(`No AliExpress order mapping for ${order.orderId || order.id}.`);
        return;
      }
      await API.post("/aliexpress-tracking/fetch-many", {
        order_ids: aeOrderIds,
        orderMongoId: order.id,
        orderId: order.orderId || null,
        language: "en_US",
      });
      setRefreshMsg(`Tracking refreshed for ${order.orderId || order.id}.`);
      await load(page);
    } catch (err) {
      setRefreshMsg(
        `Refresh failed for ${order.orderId || order.id}: ${
          err?.response?.data?.message || err?.message || "unknown error"
        }`
      );
    } finally {
      setRefreshingId(null);
    }
  }, [load, page]);

  const sync = overview?.sync || null;
  const token = overview?.token || null;
  const queue = overview?.queue || null;
  const trackable = overview?.trackable || null;

  const tokenLabel = useMemo(() => {
    if (!token) return { text: "Unknown", tone: "slate" };
    if (!token.configured) return { text: "Not configured", tone: "rose" };
    if (!token.fresh) return { text: "Expired", tone: "rose" };
    return { text: "Fresh", tone: "emerald" };
  }, [token]);

  return (
    <AdminPageShell
      title="Shipping"
      description="AliExpress shipment status and tracking sync health for B2C orders."
      chips={
        <>
          <span
            className={cx(
              "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold",
              tokenLabel.tone === "emerald"
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : tokenLabel.tone === "rose"
                ? "border-rose-200 bg-rose-50 text-rose-700"
                : "border-slate-200 bg-white text-slate-600"
            )}
          >
            <Activity className="w-3.5 h-3.5" />
            AE token: {tokenLabel.text}
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
            <Clock className="w-4 h-4 text-slate-500" />
            Last sync:{" "}
            <span className="font-semibold text-slate-900">{formatRelative(sync?.lastRunAt)}</span>
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
            <Ship className="w-4 h-4 text-slate-500" />
            {pagination.total} paid+approved orders
          </span>
        </>
      }
      actions={
        <SecondaryButton type="button" onClick={() => load(page)} disabled={loading}>
          <RefreshCw className={cx("w-4 h-4", loading && "animate-spin")} /> Refresh
        </SecondaryButton>
      }
    >
      {error ? (
        <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      {/* Sync health */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MetricCard
          title="Tracking Sync"
          value={sync?.enabled ? "Enabled" : "Disabled"}
          sub={sync?.intervalHours ? `Every ${sync.intervalHours}h` : "Manual only"}
          icon={RefreshCw}
          tone={sync?.enabled ? "emerald" : "slate"}
        />
        <MetricCard
          title="Last Sync Result"
          value={sync?.lastRun ? `${sync.lastRun.succeeded}/${sync.lastRun.attempted}` : "—"}
          sub={
            sync?.lastRun
              ? `${sync.lastRun.failed} failed · ${formatRelative(sync?.lastRunAt)}`
              : "No run recorded"
          }
          icon={Activity}
          tone={sync?.lastRun && sync.lastRun.failed > 0 ? "amber" : "sky"}
        />
        <MetricCard
          title="Trackable"
          value={trackable?.orders ?? "—"}
          sub={trackable?.items ? `${trackable.items} shipment items` : null}
          icon={Package}
          tone="violet"
        />
        <MetricCard
          title="Awaiting Shipment"
          value={queue?.awaitingShipment ?? "—"}
          sub={queue ? `${queue.inTransit} in transit · ${queue.delivered} delivered` : null}
          icon={Truck}
          tone={queue?.awaitingShipment > 0 ? "amber" : "emerald"}
        />
      </div>

      {(sync?.lastError || (token && !token.fresh)) ? (
        <Card className="p-5 mb-6">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-2xl border border-rose-200 bg-rose-50 shrink-0">
              <AlertTriangle className="w-4 h-4 text-rose-700" />
            </div>
            <div className="min-w-0">
              {token && !token.fresh ? (
                <p className="text-sm font-semibold text-rose-800">
                  AliExpress tracking token is {token.configured ? "expired" : "not configured"}.
                  {token.expiresAt ? ` (expired ${formatDateTime(token.expiresAt)})` : ""} Tracking
                  sync will fail until it is refreshed.
                </p>
              ) : null}
              {sync?.lastError ? (
                <p className="mt-1 text-sm text-slate-600 break-words">
                  Last sync error: <span className="font-medium">{String(sync.lastError)}</span>
                </p>
              ) : null}
            </div>
          </div>
        </Card>
      ) : null}

      {refreshMsg ? (
        <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          {refreshMsg}
        </div>
      ) : null}

      {/* Orders table */}
      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm md:text-base font-black tracking-tight text-slate-900">
            Paid & approved orders
          </h3>
          <span className="text-xs text-slate-500">
            Page {pagination.page} of {pagination.pages}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-black uppercase tracking-wide text-slate-600">Order</th>
                <th className="px-6 py-3 text-left text-xs font-black uppercase tracking-wide text-slate-600">State</th>
                <th className="px-6 py-3 text-left text-xs font-black uppercase tracking-wide text-slate-600">Provider</th>
                <th className="px-6 py-3 text-left text-xs font-black uppercase tracking-wide text-slate-600">Last status</th>
                <th className="px-6 py-3 text-left text-xs font-black uppercase tracking-wide text-slate-600">Last event</th>
                <th className="px-6 py-3 text-right text-xs font-black uppercase tracking-wide text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {loading && !orders.length ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                    Loading orders...
                  </td>
                </tr>
              ) : !orders.length ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                    No paid & approved orders yet.
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <Link
                        to={`${ADMIN_PATHS.ORDERS_PAGES.DETAILS}/${order.id}`}
                        className="text-sm font-semibold text-slate-900 hover:text-[#D2042D] inline-flex items-center gap-1"
                      >
                        {order.orderId || order.id}
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Link>
                      <p className="text-[11px] text-slate-400">
                        {order.itemCount} item(s){order.country ? ` · ${order.country}` : ""}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={cx(
                          "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-bold",
                          STATE_TONE[order.state] || "border-slate-200 bg-slate-50 text-slate-700"
                        )}
                      >
                        {order.state === "delivered" ? (
                          <CheckCircle className="w-3 h-3" />
                        ) : (
                          <Truck className="w-3 h-3" />
                        )}
                        {STATE_LABEL[order.state] || order.state}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-900 font-medium">
                      {order.provider || "—"}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-slate-700">{order.lastStatus || "—"}</p>
                      {order.trackingNumber ? (
                        <p className="text-[11px] text-slate-400">{order.trackingNumber}</p>
                      ) : null}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {formatDateTime(order.lastEventAt)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <SecondaryButton
                        type="button"
                        onClick={() => refreshOrderTracking(order)}
                        disabled={refreshingId === order.id}
                      >
                        <RefreshCw
                          className={cx("w-4 h-4", refreshingId === order.id && "animate-spin")}
                        />
                        Refresh tracking
                      </SecondaryButton>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {pagination.pages > 1 ? (
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
            <SecondaryButton
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={loading || pagination.page <= 1}
            >
              Previous
            </SecondaryButton>
            <span className="text-sm text-slate-600">
              Page {pagination.page} of {pagination.pages}
            </span>
            <SecondaryButton
              type="button"
              onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
              disabled={loading || pagination.page >= pagination.pages}
            >
              Next
            </SecondaryButton>
          </div>
        ) : null}
      </Card>
    </AdminPageShell>
  );
}
